import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from 'utils/makeStylesAdapter';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Dialog, Typography, DialogContent, IconButton, Tooltip } from '@mui/material';
import { HeaderModal } from 'components';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import SendIcon from '@mui/icons-material/Send';
import CancelSendIcon from '@mui/icons-material/CancelScheduleSend';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GetAppIcon from '@mui/icons-material/GetApp';
import Timeline from './components/Timeline';
import TimelineEvent from './components/TimelineEvent';
import { formatDateAndHoursToBrazilian } from 'utils/date-time.utils';
import { parseServiceMessage, parseSroMessage } from './constants';
import IntimationService from 'services/intimation/intimationService';
import { isInfraction } from 'utils/isInfraction';
import ReplayIcon from '@mui/icons-material/Replay';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#00597b',
  },
  textTitle: {
    color: '#ffffff',
  },
  modal: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  closeButton: {
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#00597b',
    color: '#ffffff',
    paddingLeft: 30,
  },
  body: {
    backgroundColor: '#ededed',
    borderRadius: 5,
    padding: 10,
    marginBottom: theme.spacing(1),
  },
  label: {
    color: '#00597b',
    fontWeight: 'bold',
    fontSize: 15,
    marginBottom: theme.spacing(2),
  },
  label2: {
    color: 'gray',
    fontWeight: 'noraml',
    fontSize: 12,
  },
}));

const ModalTrackingMail = props => {
  const { open, onClose, className, trackingCode, intimation, intimationAttempt, ...rest } = props;

  const [arSteps, setArSteps] = useState([]);
  const [loading, setLoading] = useState(false);

  const parseSteps = arStep => {
    switch (arStep.step) {
      case 'ARQUIVO_SERVICO':
        return {
          ...arStep,
          icon: FindInPageIcon,
          color: '#edc654',
          title: 'Em análise',
        };
      case 'NOTIFICACAO_ARQUIVO_SERVICO':
        return {
          ...arStep,
          icon: arStep?.inconsistencyType ? CancelSendIcon : SendIcon,
          color: arStep?.inconsistencyType ? '#dd9572' : '#72badd',
          title: arStep?.inconsistencyType ? 'Inconsistência' : 'A caminho',
          tooltip: parseServiceMessage(arStep),
        };
      case 'DEVOLUCAO_AR':
        return {
          ...arStep,
          icon: ContactMailIcon,
          color: arStep.devolutionSro !== '1' ? '#c3627a' : '#62c3ab',
          title: 'Retorno',
          tooltip: parseSroMessage(arStep.devolutionSro),
        };
      case 'ARQUIVO_CANCELAMENTO':
        return {
          ...arStep,
          icon: CancelScheduleSendIcon,
          color: '#de5c4d',
          title: 'Intimação cancelada',
        };
      default:
        return { ...arStep, icon: HelpOutlineIcon, color: 'grey', title: 'Indefinido' };
    }
  };

  const parseDate = date => {
    return date ? formatDateAndHoursToBrazilian(date) : '';
  };

  const subpoenaName = intimation => {
    const inquiryClasse = intimation.inquiryInvestigated.inquiry?.classe;
    const investigated = intimation.inquiryInvestigated.investigated;
    const useRepresentantLegalName = isInfraction(inquiryClasse);
    if (useRepresentantLegalName && investigated.representantLegal) {
      return investigated.representantLegal.name;
    }
    return investigated.name;
  };

  const calcPlaceholderCount = arSteps => {
    if (!arSteps?.length) {
      return 3;
    }
    if (arSteps[arSteps.length - 1].step === 'ARQUIVO_SERVICO') {
      return 2;
    }
    if (arSteps[arSteps.length - 1].step === 'NOTIFICACAO_ARQUIVO_SERVICO') {
      return arSteps[arSteps.length - 1].inconsistencyType ? 0 : 1;
    }
    if (
      arSteps[arSteps.length - 1].step === 'DEVOLUCAO_AR' ||
      arSteps[arSteps.length - 1].step === 'ARQUIVO_CANCELAMENTO'
    ) {
      return 0;
    }
  };

  const getArSteps = () => {
    setLoading(true);
    IntimationService.getArSteps(intimationAttempt.id)
      .then(response => {
        setArSteps(
          response.data
            .sort((a, b) => {
              return new Date(a.createdAt) - new Date(b.createdAt);
            })
            .map(arStep => parseSteps(arStep)),
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getArSteps();
  }, [intimationAttempt.id]);

  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      <Dialog
        fullScreen={fullScreen}
        onClose={onClose}
        open={open}
        aria-labelledby="responsive-dialog-title"
        className={classes.modal}
        maxWidth="md"
        fullWidth
      >
        <HeaderModal onClose={onClose}>
          Acompanhamento de intimação
          {!loading && (
            <Tooltip title="Atualizar" placement="bottom">
              <IconButton
                size="small"
                onClick={() => getArSteps()}
                disabled={loading}
              >
                <ReplayIcon style={{ color: 'gray' }} />
              </IconButton>
            </Tooltip> 
          )}
        </HeaderModal>
        <DialogContent>
          <div
            style={{
              width: '100%',
              paddingTop: 5,
            }}
          >
            <Typography className={classes.label}>
              {subpoenaName(intimation) || ''}
              <br />
              {trackingCode ? (
                <>
                  <span className={classes.label2}>{trackingCode}</span>
                  <Tooltip title="Copiar código de rastreamento" placement="right">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(trackingCode);
                      }}
                    >
                      <FileCopyIcon style={{ height: '12px', color: 'gray' }} />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <span style={{ height: '16px', display: 'inline-block' }} />
              )}
            </Typography>
            {loading ? (
              <Timeline placeholderCounter={3} placeholder>
                <TimelineEvent icon={MarkunreadMailboxIcon} title="Carregando..." subtitle="" />
              </Timeline>
            ) : arSteps.length > 0 ? (
              <Timeline placeholderCounter={calcPlaceholderCount(arSteps)} placeholder>
                <TimelineEvent
                  icon={MarkunreadMailboxIcon}
                  title="Intimação solicitada"
                  subtitle={parseDate(intimation.createdAt)}
                />
                {arSteps.map((arStep, index) => (
                  <TimelineEvent
                    key={index}
                    color={arStep.color}
                    icon={arStep.icon}
                    title={arStep.title}
                    subtitle={
                      <span>
                        {arStep.tooltip && (
                          <Tooltip arrow title={arStep.tooltip} placement="top">
                            <HelpOutlineIcon
                              style={{
                                height: '16px',
                                color: arStep.devolutionSro !== '1' ? '#c3627a' : '#62c3ab',
                                verticalAlign: 'sub',
                              }}
                            />
                          </Tooltip>
                        )}
                        <Typography variant="caption">{parseDate(arStep.createdAt)}</Typography>
                        {arStep.url && (
                          <a
                            href={arStep.url}
                            style={{ position: 'relative', top: '-2px' }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Tooltip title="Baixar carta AR" placement="top">
                              <IconButton size="small">
                                <GetAppIcon
                                  style={{
                                    height: '16px',
                                    color: arStep.devolutionSro !== '1' ? '#c3627a' : '#62c3ab',
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </a>
                        )}
                      </span>
                    }
                  />
                ))}
              </Timeline>
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 50,
                  marginTop: '-35px',
                }}
              >
                <Typography variant="h6" color="textSecondary">
                  Nenhum registro encontrado, aguarde um momento. Caso o problema persista contate o suporte.
                </Typography>
              </div>
            ) }
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModalTrackingMail;
